import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSearchParams } from "react-router-dom";
import ListPage from "../../../components/ListPage";
import { LEGACY_DEFAULT_PAGE_SIZE } from "../../../constants/pagination";
import useLogisticsOrders from "../../hooks/useLogisticsOrders";
import { CodChip, LogisticsSearchInput, formatCurrency } from "../shared";
import { toast } from "react-toastify";
import { useAddModalMain } from "../../../../contexts/address-modal/AddModal";
import DeliveryCreateModal from "../../../../components/delivery/DeliveryCreateModal";
import OrderDeliveriesModal from "./OrderDeliveriesModal";
import DrxModal from "./DrxModal";
import OrderStatusFilter from "../../../components/OrderStatusFilter";
import { parseStatusFilterParam, statusesForFilters } from "../../../constants/order-status-filters";
import useOrderInvoicePrint from "../../../../components/PaymentsItemsTable/useOrderInvoicePrint";
import { getTenantId } from "../../../../utils/helpers";
import { TENANT_COMPANY_MAP } from "../../../../utils/constants";
import { DELIVERY_STATUS } from "../../../../components/delivery/constants";

// Logistics Orders tab (`.scratch/logistics-dashboard/IMPLEMENTATION.md` §10).
//
// Orders waiting to become shipments. Reads GET /userOrder/admin — the same endpoint the
// pharmacist and finance consoles read — with includeFinance, for the money columns.
//
// Columns (product owner, 2026-10-27): Order ID, Patient Name, Patient Phone Number, Address,
// Pincode, Total Order Amount, Payment, COD, Extra Items, Deliveries, Actions, Notes.
//
// The three money columns read `row.finance`, the SAME rollup the Sales Console renders
// (classes/Sales/Functions.js buildOrderFinance), so logistics and sales cannot quote two
// different numbers for one order:
//   Total Order Amount  finance.invoiceAmount  sum of non-cancelled, non-refunded items
//   Payment             finance.amountPaid     SUCCESS payments only
//   COD                 finance.codStatus      from live (PENDING/SUCCESS) COD payments
//
// Row CTAs:
//   Create Delivery — opens the SAME DeliveryCreateModal the legacy order sidebar opens
//     (src/pages/all-orders/components/rightsidebar/rightsidebar_details.jsx:225), with the
//     same props and the same address guard. Not a reimplementation: one modal, two entry
//     points, so DTDC/Shiprocket/manual behaviour cannot drift between them.
//   Print DRX — the legacy DRX page in an overlay over this list (./DrxModal), not a new
//     tab: browsers cannot open a tab without switching to it.
//   Print Invoices — the order sidebar's Print Invoices flow (slip name/phone picker, then
//     invoices + address slip), via the shared useOrderInvoicePrint hook. Hidden for the
//     EAZY tenant, exactly as the sidebar hides it.

const PARAM = { page: "page", search: "search", status: "status" };

// An order "has a delivery" once a shipment exists against it that was not cancelled.
// A cancelled delivery leaves the order still needing one, so it does not count.
// `deliveries` is the full hasMany array GET /userOrder/admin already joins
// (classes/UserOrder/Functions.js getAdmin) — no extra request.
const hasActiveDelivery = (row) =>
    (row?.deliveries || []).some((delivery) => delivery?.status !== DELIVERY_STATUS.CANCELLED);

const textOrEmpty = (value) => (value === null || value === undefined || value === "" ? "—" : value);

// Patient and user are attached from the MyTatva user service after the query, so either
// can be missing. The phone falls back to the account holder's: a dependent patient
// (a parent on a child's account) usually has no number of their own, and the account
// holder is who the courier should call.
const patientOf = (row) => row?.mapUserPatient?.Patient || row?.mapUserPatient?.User;
const patientPhone = (row) =>
    row?.mapUserPatient?.Patient?.contact_no || row?.mapUserPatient?.User?.contact_no || row?.address?.phone;

// Pincode has its own column, so it is left out here.
const formatAddress = (address) =>
    [address?.line, address?.landmark, address?.district, address?.state]
        .map((part) => (part == null ? "" : String(part).trim()))
        .filter(Boolean)
        .join(", ");

const COD_STATUS = { TO_BE_COLLECTED: "TO_BE_COLLECTED", COLLECTED: "COLLECTED" };

// "Yes" whenever the order has a live COD payment, with what is still to collect beneath
// it. finance.isCod is false for FAILED/EXPIRED-only COD attempts (classes/Sales/codLookup.js).
function OrderCodCell({ finance }) {
    if (!finance?.isCod) return <CodChip isCod={false} />;
    return (
        <div className="flex flex-col items-start gap-1">
            <CodChip isCod />
            <span className="text-xs text-gray-500">
                {finance.codStatus === COD_STATUS.TO_BE_COLLECTED
                    ? `${formatCurrency(finance.codAmountToBeCollected)} to collect`
                    : "Collected"}
            </span>
        </div>
    );
}
OrderCodCell.propTypes = { finance: PropTypes.object };

function LogisticsOrdersPage() {
    const { AddopenModal, AddcloseModal } = useAddModalMain();
    const [searchParams, setSearchParams] = useSearchParams();
    const page = Math.max(Number(searchParams.get(PARAM.page)) || 1, 1);
    const search = searchParams.get(PARAM.search) || "";

    const statusFilters = parseStatusFilterParam(searchParams.get(PARAM.status));
    const query = useLogisticsOrders({
        page,
        size: LEGACY_DEFAULT_PAGE_SIZE,
        search,
        statuses: statusesForFilters(statusFilters),
    });
    const rows = query.data?.orders ?? [];
    const pagination = query.data?.pagination ?? { page, size: LEGACY_DEFAULT_PAGE_SIZE, total: 0 };

    // Same guard the legacy sidebar applies before opening this modal: without a real
    // address the courier forms have nothing to ship to, and the failure downstream is far
    // less legible than this message.
    const openDeliveryModal = React.useCallback(
        (row) => {
            const addressId = parseInt(row?.addressId, 10);
            if (!addressId || addressId <= 0 || !row?.address) {
                toast.error("Address is not available");
                return;
            }
            AddopenModal(() => (
                <DeliveryCreateModal
                    orderId={parseInt(row.id, 10)}
                    mapUserPatient={row.mapUserPatient}
                    address={row.address}
                    orderData={row}
                    onClose={AddcloseModal}
                />
            ));
        },
        [AddopenModal, AddcloseModal]
    );

    const { startPrint, printUi } = useOrderInvoicePrint();
    const [drxOrderId, setDrxOrderId] = useState(null);
    const canPrintInvoices = getTenantId() !== TENANT_COMPANY_MAP.EAZY;

    const openDeliveries = React.useCallback(
        (row) => {
            AddopenModal(() => <OrderDeliveriesModal order={row} onClose={AddcloseModal} />);
        },
        [AddopenModal, AddcloseModal]
    );

    const setParam = (key, value, { resetPage = true } = {}) => {
        const next = new URLSearchParams(searchParams);
        if (value === "" || value == null) next.delete(key);
        else next.set(key, String(value));
        if (resetPage) next.delete(PARAM.page);
        setSearchParams(next, { replace: true });
    };

    const columns = useMemo(
        () => [
            { key: "id", label: "Order ID", render: (row) => `#${row.id}` },
            {
                key: "patientName",
                label: "Patient Name",
                wrap: true,
                render: (row) => (
                    <span className="font-medium">{textOrEmpty(patientOf(row)?.name)}</span>
                ),
            },
            {
                key: "patientPhone",
                label: "Patient Phone Number",
                render: (row) => textOrEmpty(patientPhone(row)),
            },
            {
                key: "address",
                label: "Address",
                wrap: true,
                render: (row) => {
                    const text = formatAddress(row.address);
                    return text ? <span className="block max-w-[280px]">{text}</span> : "—";
                },
            },
            {
                key: "pincode",
                label: "Pincode",
                render: (row) => textOrEmpty(row.address?.pincode),
            },
            {
                key: "totalAmount",
                label: "Total Order Amount (₹)",
                align: "right",
                // finance.invoiceAmount: payable paymentItems only — CANCELLED, REFUNDED,
                // PARTIALLY_REFUNDED, TO_BE_REFUNDED and credit notes are excluded
                // (classes/Sales/financeSql.js). Falls back to userOrders.total for orders
                // that predate paymentItems.
                render: (row) => formatCurrency(row.finance?.invoiceAmount ?? row.total),
            },
            {
                key: "payment",
                label: "Payment (₹)",
                align: "right",
                // finance.amountPaid: SUCCESS payments only. PENDING COD is not money in
                // hand, so it is shown in the COD column, never counted here.
                render: (row) => formatCurrency(row.finance?.amountPaid ?? 0),
            },
            {
                key: "cod",
                label: "COD",
                render: (row) => <OrderCodCell finance={row.finance} />,
            },
            {
                key: "extraItems",
                label: "Extra Items",
                wrap: true,
                // Set from the order edit page's "Add Extra Items" modal, stored at
                // additionalDetails.extraItems.
                render: (row) => {
                    const value = row.additionalDetails?.extraItems;
                    if (!value) return "—";
                    return (
                        <span className="inline-flex items-start gap-1 text-amber-700">
                            <span aria-hidden="true">⚠</span>
                            <span>{value}</span>
                        </span>
                    );
                },
            },
            {
                key: "deliveries",
                label: "Deliveries",
                align: "center",
                // Every delivery on the order, cancelled ones included -- the modal shows
                // each with its status. The row's green tint is the "has a live delivery"
                // signal; this is the full count.
                render: (row) => {
                    const count = (row.deliveries || []).length;
                    if (count === 0) return "0";
                    return (
                        <button
                            type="button"
                            onClick={() => openDeliveries(row)}
                            className="font-semibold text-[#00A48B] underline hover:text-[#008f79]"
                        >
                            {count}
                        </button>
                    );
                },
            },
            {
                key: "actions",
                label: "Actions",
                wrap: true,
                render: (row) => (
                    <div className="flex min-w-[150px] flex-col gap-1">
                        <button
                            type="button"
                            onClick={() => openDeliveryModal(row)}
                            className="rounded-lg bg-[#00A48B] px-3 py-2 font-sans text-sm font-semibold text-white-50 transition-colors hover:bg-[#008f79]"
                        >
                            Create Delivery
                        </button>
                        <button
                            type="button"
                            onClick={() => setDrxOrderId(row.id)}
                            className="rounded-lg border border-[#00A48B] px-3 py-2 font-sans text-sm font-semibold text-[#00A48B] transition-colors hover:bg-[#00A48B] hover:text-white-50"
                        >
                            Print DRX
                        </button>
                        {canPrintInvoices && (
                            <button
                                type="button"
                                onClick={() => startPrint(row)}
                                className="rounded-lg border border-[#00A48B] px-3 py-2 font-sans text-sm font-semibold text-[#00A48B] transition-colors hover:bg-[#00A48B] hover:text-white-50"
                            >
                                Print Invoices
                            </button>
                        )}
                    </div>
                ),
            },
            {
                key: "notes",
                label: "Notes",
                wrap: true,
                // userOrders.notes is a single TEXT field with no source attribution. The
                // prototype colour-codes Pharmacist vs Sales from a "Pharmacist: " prefix in
                // the string; nothing writes such a prefix today, so the note is rendered
                // as-is rather than faking a tag.
                render: (row) => {
                    if (!row.notes) return "—";
                    return <span className="block max-w-[280px]">{row.notes}</span>;
                },
            },
        ],
        [openDeliveryModal, openDeliveries, canPrintInvoices, startPrint]
    );

    const filters = (
        <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
                <button
                    type="button"
                    onClick={() => query.refetch()}
                    className="rounded-lg bg-[#00A48B] px-4 py-2 font-sans text-sm font-semibold text-white-50 hover:bg-[#008f79]"
                >
                    Refresh
                </button>
                <LogisticsSearchInput value={search} onSearch={(term) => setParam(PARAM.search, term)} />
                <span className="text-sm text-[var(--ops-carbon-500)]">
                    {pagination.total} {pagination.total === 1 ? "order" : "orders"}
                </span>
                <span className="ml-auto inline-flex items-center gap-2 text-sm text-[var(--ops-carbon-500)]">
                    <span aria-hidden="true" className="h-3 w-3 rounded-sm border border-emerald-300 bg-emerald-100" />
                    Delivery created
                </span>
            </div>
            <OrderStatusFilter
                selected={statusFilters}
                onChange={(ids) => setParam(PARAM.status, ids.join(","))}
            />
        </div>
    );

    return (
        <>
        <ListPage
            title="Orders"
            subtitle="Orders waiting to be packed and shipped."
            filters={filters}
            columns={columns}
            rows={rows}
            rowClassName={(row) => (hasActiveDelivery(row) ? "bg-emerald-100" : "")}
            query={query}
            pagination={pagination}
            onPageChange={(next) => setParam(PARAM.page, next, { resetPage: false })}
            emptyTitle="No orders"
            emptyHint="Try clearing the search."
            testId="logistics-orders"
        />
        {printUi}
        {drxOrderId && <DrxModal orderId={drxOrderId} onClose={() => setDrxOrderId(null)} />}
        </>
    );
}

export default LogisticsOrdersPage;
